/**
 * SOLID Review
 * S — Strong. One concern: the room on the server, as the contracted `ClientRoomServer` port.
 * O — Good. A new room op (create and join arrive in FE2) is one method over the adapter.
 * L — Strong. Implements `IPortClientRoomServer` exactly.
 * I — Strong. Exactly the port's ops, each one call to the adapter.
 * D — Strong. The Application's adapter interface; the Apollo adapter arrives by constructor.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPayloadRoomRead } from '../../domain/ports/schemas/TableMoves';
import type { IPortClientRoomServer, IPayloadServerRoomCode } from '../../application/ports/contracts';
import type { IAdapterApolloRoom } from '../../application/ports/IAdapterApolloRoom';
import type { ICommandLeaveRoom, IEntityRawRoom } from '../../application/ports/apolloShapes';
import type {
  IPayloadSeat, IPayloadLateJoin, IPayloadRejoinRequest, IPayloadHost, IPayloadPlayer, IPayloadSeatRoom,
} from '../../domain/ports/schemas/SessionMoves';
import { fromAdapter, doneFromAdapter } from './wireResult';

/** A `Room` answer as fresh JSON: every list copied, nothing else touched — the contract parses the rest. */
function seatRoomOf(room: IEntityRawRoom): IPayloadSeatRoom {
  return {
    id: room.id,
    roomCode: room.roomCode,
    playerIds: [...room.playerIds],
    players: room.players.map((p) => ({ id: p.id, displayName: p.displayName })),
    status: room.status,
  };
}

/**
 * THE ROOM GATEWAY (frontend strict-ports plan, FE1, 2026-10-03), over `ApolloRoomAdapter`. Lists are
 * copied: the adapter's are readonly, and the port's answers are fresh JSON. FE2 adds the five ways to a
 * seat — create, join, late join, rejoin by code, and the cold login's "where does the server seat me".
 */
export class ClientRoomServerAdapter implements IPortClientRoomServer {
  constructor(private readonly rooms: IAdapterApolloRoom) {}

  async getRoom(_ctx: IContextRequest, read: IPayloadRoomRead) {
    return fromAdapter(await this.rooms.getRoom(read.roomId), (room) => ({ id: room.id, players: [...room.players] }));
  }

  async getRoomByCode(_ctx: IContextRequest, query: IPayloadServerRoomCode) {
    return fromAdapter(await this.rooms.getRoomByCode(query.roomCode), (lookup) =>
      (lookup === null ? null : { ...lookup, suspendedPlayers: [...lookup.suspendedPlayers] }));
  }

  async leaveRoom(_ctx: IContextRequest, seat: { readonly roomId: string; readonly playerId: string }) {
    const command: ICommandLeaveRoom = { roomId: seat.roomId, playerId: seat.playerId };
    return doneFromAdapter(await this.rooms.leaveRoom(command));
  }

  async createRoom(_ctx: IContextRequest, request: IPayloadHost) {
    return fromAdapter(await this.rooms.createRoom(request.hostPlayerId), seatRoomOf);
  }

  async joinRoom(_ctx: IContextRequest, seat: IPayloadSeat) {
    return fromAdapter(await this.rooms.joinRoom(seat.roomId, seat.playerId), seatRoomOf);
  }

  async lateJoinRoom(_ctx: IContextRequest, request: IPayloadLateJoin) {
    return fromAdapter(await this.rooms.lateJoinRoom(request.roomId, request.playerId, request.displayName, request.matchCredits), seatRoomOf);
  }

  async rejoinRoomByCode(_ctx: IContextRequest, request: IPayloadRejoinRequest) {
    return fromAdapter(await this.rooms.rejoinRoomByCode(request.roomCode, request.playerId, request.displayName, request.email), (rejoin) => ({
      playerId: rejoin.playerId,
      roomId: rejoin.roomId,
      roomCode: rejoin.roomCode,
      roomStatus: rejoin.roomStatus,
      players: rejoin.players.map((p) => ({ id: p.id, displayName: p.displayName })),
    }));
  }

  async getActiveRoomForPlayer(_ctx: IContextRequest, request: IPayloadPlayer) {
    return fromAdapter(await this.rooms.getActiveRoomForPlayer(request.playerId), (room) =>
      (room === null ? null : { roomId: room.roomId, roomCode: room.roomCode, roomStatus: room.roomStatus, playerCount: room.playerCount }));
  }
}
